import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = ({ user, setUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      navigate('/dashboard');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= 8) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.get(`${API}/stripe/checkout-status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.payment_status === 'paid' || res.data.payment_status === 'no_payment_required' || res.data.subscription_status === 'active') {
        setStatus('success');
        // Update local user data with active/trialing status
        if (user) {
          const subStatus = res.data.subscription_status === 'active' ? 'active' : 'trialing';
          const updatedUser = { ...user, subscription_status: subStatus, plan_type: res.data.plan_type || user.plan_type || 'basic' };
          localStorage.setItem('kaplie_user', JSON.stringify(updatedUser));
          if (setUser) setUser(updatedUser);
        }
        setTimeout(() => navigate('/dashboard'), 2500);
        return;
      }

      if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Keep polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch (e) {
      console.error('Status check error:', e);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="fixed inset-0 bg-gradient-to-br from-[#D4AF37]/[0.04] via-black to-violet-900/[0.06] pointer-events-none" />
      <div className="relative z-10 text-center max-w-md px-6">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Processing your payment...</h1>
            <p className="text-white/50">This will only take a moment</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Welcome to Kaplie Pro!</h1>
            <p className="text-white/50 mb-2">Your 3-day free trial has started</p>
            <p className="text-white/40 text-sm">Redirecting to dashboard...</p>
          </>
        )}
        {status === 'timeout' && (
          <>
            <h1 className="text-2xl font-bold mb-2">Payment is processing</h1>
            <p className="text-white/50 mb-6">It may take a minute to confirm. Check your email for confirmation.</p>
            <button onClick={() => navigate('/dashboard')} className="px-8 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-xl font-semibold">
              Go to Dashboard
            </button>
          </>
        )}
        {status === 'expired' && (
          <>
            <h1 className="text-2xl font-bold mb-2">Session Expired</h1>
            <p className="text-white/50 mb-6">Your payment session expired. Please try again.</p>
            <button onClick={() => navigate('/dashboard')} className="px-8 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-xl font-semibold">
              Go Back
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
